/**
 * Procedural Web Audio for Vault Breaker — no external sound files.
 *
 * Cues:
 *   - playKeyClick(): metallic "click-clack" when a key turns in a lock (~140ms)
 *   - playLockCrack(): heavier "crack/shatter" when a lock fully breaks (~220ms)
 *   - playDoorSwing(): long resonant metallic groan (~1.8s)
 *   - playWinSting(): triumphant perfect-fifth dyad (~400ms)
 *   - playBustThud(): sub-bass thud when bust banner lands (~600ms)
 *   - playTick(urgent?): short clock tick for round timers (~40ms)
 *
 * The browser autoplay policy requires that AudioContext be created from a
 * user gesture. Call initAudio() from a click handler (we wire it to the
 * PLACE BET button). Subsequent calls are no-ops.
 *
 * Honours prefers-reduced-motion — no sound is played for users who opted
 * out of motion. Same toggle used for animations elsewhere in the app.
 */

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let muted = false;

function reducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Toggle every play function to silently no-op. Persisted by the store. */
export function setSoundMuted(v: boolean): void {
  muted = v;
}

export function isSoundMuted(): boolean {
  return muted;
}

function silenced(): boolean {
  return muted || reducedMotion();
}

export function initAudio(): void {
  if (ctx || typeof window === "undefined") return;
  const W = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  const Ctor = W.AudioContext ?? W.webkitAudioContext;
  if (!Ctor) return;
  try {
    ctx = new Ctor();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.28;

    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -20;
    compressor.ratio.value = 4;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.15;

    masterGain.connect(compressor);
    compressor.connect(ctx.destination);
  } catch {
    ctx = null;
    masterGain = null;
  }
}

function ready(): { ctx: AudioContext; out: GainNode } | null {
  if (!ctx || !masterGain) return null;
  if (ctx.state === "suspended") void ctx.resume().catch(() => {});
  return { ctx, out: masterGain };
}

function jitter(base: number, pct: number): number {
  return base * (1 + (Math.random() - 0.5) * 2 * pct);
}

function noiseBuffer(ctx: AudioContext, durMs: number): AudioBuffer {
  const len = Math.max(1, Math.floor(ctx.sampleRate * (durMs / 1000)));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

/**
 * Quick metallic click-clack. Fired each time a key turns in a lock during
 * the chest cinematic.
 *
 * Recipe (per audio agent recommendation):
 *   - Highpassed noise burst @ ~3.5kHz, 60ms
 *   - Sine click @ ~2200Hz, 50ms decay
 *   - Sine clack with downward pitch sweep 1400→800Hz, 80ms
 *   - Light biquad shaping for "metal" character
 *   - ±5% jitter so 5 keys don't sound identical
 */
export function playKeyClick(): void {
  if (silenced()) return;
  const r = ready();
  if (!r) return;
  const { ctx: c, out } = r;
  const t = c.currentTime;

  // 1. Noise burst (metallic scrape)
  const noise = c.createBufferSource();
  noise.buffer = noiseBuffer(c, 60);
  const noiseHp = c.createBiquadFilter();
  noiseHp.type = "highpass";
  noiseHp.frequency.value = jitter(3500, 0.07);
  noiseHp.Q.value = 0.6;
  const noiseGain = c.createGain();
  noiseGain.gain.setValueAtTime(0, t);
  noiseGain.gain.linearRampToValueAtTime(0.7, t + 0.005);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
  noise.connect(noiseHp).connect(noiseGain).connect(out);
  noise.start(t);
  noise.stop(t + 0.07);

  // 2. Sine click — main body of the click
  const click = c.createOscillator();
  click.type = "sine";
  click.frequency.value = jitter(2200, 0.05);
  const clickGain = c.createGain();
  clickGain.gain.setValueAtTime(0, t);
  clickGain.gain.linearRampToValueAtTime(0.5, t + 0.005);
  clickGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
  click.connect(clickGain).connect(out);
  click.start(t);
  click.stop(t + 0.06);

  // 3. Sine clack with descending pitch sweep
  const clack = c.createOscillator();
  clack.type = "sine";
  const clackHi = jitter(1400, 0.05);
  clack.frequency.setValueAtTime(clackHi, t + 0.06);
  clack.frequency.linearRampToValueAtTime(800, t + 0.14);
  const clackGain = c.createGain();
  clackGain.gain.setValueAtTime(0, t + 0.06);
  clackGain.gain.linearRampToValueAtTime(0.4, t + 0.068);
  clackGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
  // Light shaping
  const peak = c.createBiquadFilter();
  peak.type = "peaking";
  peak.frequency.value = 4000;
  peak.Q.value = 4;
  peak.gain.value = 6;
  clack.connect(peak).connect(clackGain).connect(out);
  clack.start(t + 0.06);
  clack.stop(t + 0.16);
}

/**
 * Heavier "lock shatter" sound — used when a lock fully cracks during the
 * live game (server emitted a crack event).
 */
export function playLockCrack(): void {
  if (silenced()) return;
  const r = ready();
  if (!r) return;
  const { ctx: c, out } = r;
  const t = c.currentTime;

  // Extended noise burst, sharper highpass
  const noise = c.createBufferSource();
  noise.buffer = noiseBuffer(c, 100);
  const noiseHp = c.createBiquadFilter();
  noiseHp.type = "highpass";
  noiseHp.frequency.value = 5000;
  const nGain = c.createGain();
  nGain.gain.setValueAtTime(0, t);
  nGain.gain.linearRampToValueAtTime(0.85, t + 0.005);
  nGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.1);
  noise.connect(noiseHp).connect(nGain).connect(out);
  noise.start(t);
  noise.stop(t + 0.12);

  // Sine + square octave with descending sweep 3100 → 600 Hz over 220ms
  const fund = c.createOscillator();
  fund.type = "sine";
  fund.frequency.setValueAtTime(jitter(3100, 0.04), t);
  fund.frequency.linearRampToValueAtTime(600, t + 0.22);
  const fundGain = c.createGain();
  fundGain.gain.setValueAtTime(0, t);
  fundGain.gain.linearRampToValueAtTime(0.45, t + 0.008);
  fundGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
  fund.connect(fundGain).connect(out);
  fund.start(t);
  fund.stop(t + 0.24);

  const square = c.createOscillator();
  square.type = "square";
  square.frequency.setValueAtTime(jitter(1550, 0.04), t);
  square.frequency.linearRampToValueAtTime(300, t + 0.22);
  const sqGain = c.createGain();
  sqGain.gain.setValueAtTime(0, t);
  sqGain.gain.linearRampToValueAtTime(0.18, t + 0.008);
  sqGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
  const tame = c.createBiquadFilter();
  tame.type = "lowpass";
  tame.frequency.value = 4000;
  square.connect(tame).connect(sqGain).connect(out);
  square.start(t);
  square.stop(t + 0.22);
}

/**
 * Long resonant metallic groan — used when the vault door swings open during
 * the cinematic. ~1.8s.
 *
 * Recipe:
 *   - Sawtooth body @ 110Hz (A2) modulated by a slow LFO sweep 60→200Hz
 *   - Lowpass filter sweeping 400Hz → 1500Hz over the first 1.0s
 *   - Filtered noise component for grit (highpassed @ 800Hz)
 *   - Gain envelope: 0 → 0.3 attack 0.2s, sustain 0.8s, release 0.8s
 */
export function playDoorSwing(): void {
  if (silenced()) return;
  const r = ready();
  if (!r) return;
  const { ctx: c, out } = r;
  const t = c.currentTime;

  // Sawtooth body with a slow LFO modulating its pitch 60→200Hz.
  const body = c.createOscillator();
  body.type = "sawtooth";
  body.frequency.setValueAtTime(jitter(110, 0.04), t);

  const lfo = c.createOscillator();
  lfo.type = "sine";
  lfo.frequency.value = 0.7;
  const lfoGain = c.createGain();
  // Sweep amplitude in Hz around the carrier — covers ~60-200Hz envelope.
  lfoGain.gain.setValueAtTime(70, t);
  lfo.connect(lfoGain).connect(body.frequency);

  // Lowpass sweep 400Hz → 1500Hz over the first second.
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.Q.value = 1.4;
  lp.frequency.setValueAtTime(400, t);
  lp.frequency.linearRampToValueAtTime(1500, t + 1.0);
  lp.frequency.linearRampToValueAtTime(900, t + 1.8);

  // Body gain envelope — 0.2s attack, 0.8s sustain, 0.8s release (1.8s total).
  const bodyGain = c.createGain();
  bodyGain.gain.setValueAtTime(0, t);
  bodyGain.gain.linearRampToValueAtTime(0.3, t + 0.2);
  bodyGain.gain.linearRampToValueAtTime(0.3, t + 1.0);
  bodyGain.gain.linearRampToValueAtTime(0.0001, t + 1.8);

  body.connect(lp).connect(bodyGain).connect(out);
  body.start(t);
  body.stop(t + 1.85);
  lfo.start(t);
  lfo.stop(t + 1.85);

  // Grit noise component — highpassed for that metal-on-metal scrape texture.
  const noise = c.createBufferSource();
  noise.buffer = noiseBuffer(c, 1800);
  const noiseHp = c.createBiquadFilter();
  noiseHp.type = "highpass";
  noiseHp.frequency.value = 800;
  noiseHp.Q.value = 0.7;
  const noiseLp = c.createBiquadFilter();
  noiseLp.type = "lowpass";
  noiseLp.frequency.setValueAtTime(2200, t);
  noiseLp.frequency.linearRampToValueAtTime(3500, t + 1.0);
  const noiseGain = c.createGain();
  noiseGain.gain.setValueAtTime(0, t);
  noiseGain.gain.linearRampToValueAtTime(0.08, t + 0.25);
  noiseGain.gain.linearRampToValueAtTime(0.08, t + 1.0);
  noiseGain.gain.linearRampToValueAtTime(0.0001, t + 1.8);
  noise.connect(noiseHp).connect(noiseLp).connect(noiseGain).connect(out);
  noise.start(t);
  noise.stop(t + 1.85);
}

/**
 * Triumphant perfect-fifth dyad — used at the moment "CASHED OUT" appears.
 * ~400ms.
 *
 * Recipe:
 *   - Sine @ C5 (523Hz) + triangle @ G5 (784Hz) — perfect fifth
 *   - Quick attack 0.02s, decay 0.4s
 *   - Final volume 0.4
 */
export function playWinSting(): void {
  if (silenced()) return;
  const r = ready();
  if (!r) return;
  const { ctx: c, out } = r;
  const t = c.currentTime;

  // Master mix gain so both notes share a single envelope.
  const mix = c.createGain();
  mix.gain.setValueAtTime(0, t);
  mix.gain.linearRampToValueAtTime(0.4, t + 0.02);
  mix.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
  mix.connect(out);

  // C5 — sine root.
  const root = c.createOscillator();
  root.type = "sine";
  root.frequency.value = 523.25;
  const rootGain = c.createGain();
  rootGain.gain.value = 0.7;
  root.connect(rootGain).connect(mix);
  root.start(t);
  root.stop(t + 0.45);

  // G5 — triangle fifth.
  const fifth = c.createOscillator();
  fifth.type = "triangle";
  fifth.frequency.value = 783.99;
  const fifthGain = c.createGain();
  fifthGain.gain.value = 0.55;
  fifth.connect(fifthGain).connect(mix);
  fifth.start(t);
  fifth.stop(t + 0.45);
}

/**
 * Sub-bass thud — used when the bust banner lands. ~600ms.
 *
 * Recipe:
 *   - Sine @ C2 (65Hz) pitch-bent down to 35Hz over the first 0.4s
 *   - Filtered noise burst (lowpass 200Hz) for the impact body
 *   - Gain 0 → 0.5 attack 0.01s, exponential decay 0.6s
 */
export function playBustThud(): void {
  if (silenced()) return;
  const r = ready();
  if (!r) return;
  const { ctx: c, out } = r;
  const t = c.currentTime;

  // Sub-sine with a pitch bend — gives the chest "wump" feel.
  const sub = c.createOscillator();
  sub.type = "sine";
  sub.frequency.setValueAtTime(65, t);
  sub.frequency.linearRampToValueAtTime(35, t + 0.4);
  const subGain = c.createGain();
  subGain.gain.setValueAtTime(0, t);
  subGain.gain.linearRampToValueAtTime(0.5, t + 0.01);
  subGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.6);
  sub.connect(subGain).connect(out);
  sub.start(t);
  sub.stop(t + 0.65);

  // Filtered noise for impact body.
  const noise = c.createBufferSource();
  noise.buffer = noiseBuffer(c, 200);
  const noiseLp = c.createBiquadFilter();
  noiseLp.type = "lowpass";
  noiseLp.frequency.value = 200;
  noiseLp.Q.value = 0.7;
  const noiseGain = c.createGain();
  noiseGain.gain.setValueAtTime(0, t);
  noiseGain.gain.linearRampToValueAtTime(0.35, t + 0.005);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
  noise.connect(noiseLp).connect(noiseGain).connect(out);
  noise.start(t);
  noise.stop(t + 0.22);
}

/**
 * Clock tick — short metronome click that BUILDS SUSPENSE as time runs out.
 *
 * Recipe (intensity 0..1):
 *   - Sine bell @ 1500Hz (calm) → 2700Hz (panic) for the body pitch
 *   - High-pass noise burst (4kHz) for the click attack
 *   - Decay tail length 40ms → 110ms (longer ring = more drama)
 *   - Gain peak 0.08 → 0.26 (louder as time runs out)
 *   - Sub-bass sawtooth drone @ 80Hz fades in once intensity > 0.3,
 *     giving the "ticking time bomb" rumble in the back half of the round.
 *   - Above intensity > 0.5, a downward pitch sweep on the bell adds a
 *     dread-fall feel.
 *
 * intensity is clamped to [0, 1] internally. Call site computes
 *   intensity = 1 - (timeLeft / ROUND_TIME_S).
 */
export function playTick(intensity = 0): void {
  if (silenced()) return;
  const r = ready();
  if (!r) return;
  const { ctx: c, out } = r;
  const t = c.currentTime;

  const i = Math.max(0, Math.min(1, intensity));
  const lerp = (a: number, b: number, x: number): number => a + (b - a) * x;

  const sineFreq = lerp(1500, 2700, i);
  // Gain peak ranges 0.22 (calm) → 0.50 (panic). Calibrated against the
  // other game cues (key click ≈0.4, bust thud ≈0.5) so the tick is
  // present from the first second and dominant in the final stretch.
  const gainPeak = lerp(0.22, 0.50, i);
  const decay = lerp(0.05, 0.13, i);

  // Sine bell — short attack, decay scales with intensity (longer ring tail
  // = more drama). Above i > 0.5, the pitch sweeps DOWN over the decay,
  // adding a dread-fall feel.
  const tone = c.createOscillator();
  tone.type = "sine";
  tone.frequency.setValueAtTime(sineFreq, t);
  if (i > 0.5) {
    tone.frequency.linearRampToValueAtTime(sineFreq * 0.85, t + decay);
  }
  const toneGain = c.createGain();
  toneGain.gain.setValueAtTime(0, t);
  toneGain.gain.linearRampToValueAtTime(gainPeak, t + 0.002);
  toneGain.gain.exponentialRampToValueAtTime(0.0001, t + decay);
  tone.connect(toneGain).connect(out);
  tone.start(t);
  tone.stop(t + decay + 0.01);

  // Hi-pass noise burst at attack — percussive edge
  const noise = c.createBufferSource();
  noise.buffer = noiseBuffer(c, 80);
  const noiseHp = c.createBiquadFilter();
  noiseHp.type = "highpass";
  noiseHp.frequency.value = 4000;
  const noiseGain = c.createGain();
  noiseGain.gain.setValueAtTime(0, t);
  // Noise attack peak: 0.18 (calm) → 0.32 (panic). Crisper percussive edge.
  noiseGain.gain.linearRampToValueAtTime(0.18 + i * 0.14, t + 0.001);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.02);
  noise.connect(noiseHp).connect(noiseGain).connect(out);
  noise.start(t);
  noise.stop(t + 0.025);

  // Sub-bass drone — fades in once intensity > 0.3. Saw @ 80Hz with a
  // lowpass filter, gain scales with intensity. This is the "ticking time
  // bomb" rumble that builds dread underneath the bell.
  if (i > 0.3) {
    const drone = c.createOscillator();
    drone.type = "sawtooth";
    drone.frequency.value = 80;
    const droneLp = c.createBiquadFilter();
    droneLp.type = "lowpass";
    droneLp.frequency.value = 220;
    droneLp.Q.value = 1.2;
    const droneGain = c.createGain();
    // Drone peak: 0 at i=0.3 → 0.14 at i=1.0. Sub-bass rumble that builds
    // dread under the bell, now loud enough to actually feel.
    const dronePeak = (i - 0.3) * 0.20;
    droneGain.gain.setValueAtTime(0, t);
    droneGain.gain.linearRampToValueAtTime(dronePeak, t + 0.04);
    droneGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    drone.connect(droneLp).connect(droneGain).connect(out);
    drone.start(t);
    drone.stop(t + 0.55);
  }
}
